import * as React from "react";
import { LayoutGrid } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/** Swatch + hex field + native picker + preset palette popover. */
export function ColorField({
  value,
  onChange,
  swatches = [],
}: {
  value: string;
  onChange: (value: string) => void;
  swatches?: string[];
}) {
  const nativeRef = React.useRef<HTMLInputElement>(null);

  return (
    <div className="flex items-center gap-2.5">
      {/* Swatch — opens the OS color picker */}
      <button
        type="button"
        aria-label="Pick color"
        onClick={() => nativeRef.current?.click()}
        className="relative size-10 shrink-0 cursor-pointer rounded-[10px] border border-border-strong shadow-[inset_0_0_0_2px_rgba(255,255,255,.08)]"
        style={{ background: value }}
      >
        <input
          ref={nativeRef}
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 size-full cursor-pointer opacity-0"
        />
      </button>

      {/* Hex */}
      <Input
        value={value.toUpperCase()}
        onChange={(e) => {
          let v = e.target.value;
          if (!v.startsWith("#")) v = "#" + v;
          onChange(v);
        }}
        className="font-mono text-[13px] tracking-[.04em]"
      />

      {/* Presets */}
      <Popover>
        <PopoverTrigger
          aria-label="Preset swatches"
          className="grid size-10 shrink-0 place-items-center rounded-[10px] border border-border bg-panel-2 text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring"
        >
          <LayoutGrid className="size-4" />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto">
          <div className="flex max-w-[208px] flex-wrap gap-2">
            {swatches.map((s) => (
              <button
                key={s}
                type="button"
                title={s}
                onClick={() => onChange(s)}
                className={cn(
                  "size-[26px] cursor-pointer rounded-[7px] border",
                  value.toLowerCase() === s.toLowerCase()
                    ? "border-2 border-foreground"
                    : "border-border-strong",
                )}
                style={{ background: s }}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
